import { Modal, Form, Button, Row, Col, Accordion, Alert, Spinner } from "react-bootstrap";
import { FaTimes, FaPlus, FaSave, FaPlay, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { APIToolConfig } from "./ToolsConfiguration";
import { useState } from "react";

interface ParameterConfig {
    value: string;
    required: boolean;
    type: "user-input" | "system-value";
    description?: string;
}

interface ApiToolModalProps {
    show: boolean;
    onHide: () => void;
    editingTool: APIToolConfig | null;
    newTool: APIToolConfig;
    setNewTool: (tool: APIToolConfig) => void;
    onSave: () => void;
    onUpdate: () => void;
    addKeyValuePair: (field: "headers" | "params" | "data") => void;
    updateKeyValuePair: (
        field: "headers" | "params" | "data",
        oldKey: string,
        newKey: string,
        config: ParameterConfig,
    ) => void;
    removeKeyValuePair: (
        field: "headers" | "params" | "data",
        key: string,
    ) => void;
}

function ApiToolModal({
    show,
    onHide,
    editingTool,
    newTool,
    setNewTool,
    onSave,
    onUpdate,
    addKeyValuePair,
    updateKeyValuePair,
    removeKeyValuePair,
}: ApiToolModalProps) {
    const [isTestingApi, setIsTestingApi] = useState(false);
    const [testResult, setTestResult] = useState<{
        success: boolean;
        status?: number;
        data?: any;
        error?: string;
    } | null>(null);

    const hasRequestBody = newTool.method === 'POST' || newTool.method === 'PUT' || newTool.method === 'PATCH';

    const testApiRequest = async () => {
        setIsTestingApi(true);
        setTestResult(null);

        try {
            const config: RequestInit = {
                method: newTool.method,
                headers: {},
            };

            // Add headers
            if (newTool.headers) {
                Object.entries(newTool.headers).forEach(([key, paramConfig]) => {
                    const paramConf = paramConfig as ParameterConfig;
                    if (paramConf.value) {
                        (config.headers as Record<string, string>)[key] = paramConf.value;
                    }
                });
            }

            // Build URL with query parameters
            let url = newTool.url;
            if (newTool.params && Object.keys(newTool.params).length > 0) {
                const searchParams = new URLSearchParams();
                Object.entries(newTool.params).forEach(([key, paramConfig]) => {
                    const paramConf = paramConfig as ParameterConfig;
                    if (paramConf.value) {
                        searchParams.append(key, paramConf.value);
                    }
                });
                if (searchParams.toString()) {
                    url += '?' + searchParams.toString();
                }
            }

            // Add request body for POST, PUT, PATCH
            if (hasRequestBody && newTool.data && Object.keys(newTool.data).length > 0) {
                const bodyData: Record<string, any> = {};
                Object.entries(newTool.data).forEach(([key, paramConfig]) => {
                    const paramConf = paramConfig as ParameterConfig;
                    if (paramConf.value) {
                        bodyData[key] = paramConf.value;
                    }
                });
                
                if (Object.keys(bodyData).length > 0) {
                    config.body = JSON.stringify(bodyData);
                    (config.headers as Record<string, string>)['Content-Type'] = 'application/json';
                }
            }

            const response = await fetch(url, config);
            const responseData = await response.text();
            
            let parsedData;
            try {
                parsedData = JSON.parse(responseData);
            } catch {
                parsedData = responseData;
            }

            setTestResult({
                success: response.ok,
                status: response.status,
                data: parsedData
            });

        } catch (error) {
            setTestResult({
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            });
        } finally {
            setIsTestingApi(false);
        }
    };

    const renderKeyValueSection = (
        field: "headers" | "params" | "data",
        title: string,
    ) => {
        // Hide request body section for GET and DELETE methods
        if (field === 'data' && !hasRequestBody) {
            return null;
        }

        return (
        <Accordion.Item
            eventKey={field}
            style={{ backgroundColor: "#1a1a1a", border: "1px solid #555" }}
        >
            <Accordion.Header style={{ backgroundColor: "#1a1a1a" }}>
                <span className="text-light">{title} (Optional)</span>
            </Accordion.Header>
            <Accordion.Body style={{ backgroundColor: "#1a1a1a" }}>
                {Object.entries(newTool[field] || {}).map(
                    ([key, config], index) => {
                        const paramConfig = config as ParameterConfig;
                        return (
                            <Row key={index} className="mb-3">
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Key"
                                        value={key}
                                        onChange={(e) =>
                                            updateKeyValuePair(
                                                field,
                                                key,
                                                e.target.value,
                                                paramConfig,
                                            )
                                        }
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Value"
                                        value={paramConfig.value}
                                        onChange={(e) =>
                                            updateKeyValuePair(
                                                field,
                                                key,
                                                key,
                                                {
                                                    ...paramConfig,
                                                    value: e.target.value,
                                                },
                                            )
                                        }
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        value={paramConfig.type}
                                        onChange={(e) =>
                                            updateKeyValuePair(
                                                field,
                                                key,
                                                key,
                                                {
                                                    ...paramConfig,
                                                    type: e.target.value as
                                                        | "user-input"
                                                        | "system-value",
                                                },
                                            )
                                        }
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    >
                                        <option value="user-input">
                                            User Input
                                        </option>
                                        <option value="system-value">
                                            System Value
                                        </option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Check
                                        type="switch"
                                        label="Required"
                                        checked={paramConfig.required}
                                        onChange={(e) =>
                                            updateKeyValuePair(
                                                field,
                                                key,
                                                key,
                                                {
                                                    ...paramConfig,
                                                    required: e.target.checked,
                                                },
                                            )
                                        }
                                        style={{ color: "#fff" }}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() =>
                                            removeKeyValuePair(field, key)
                                        }
                                    >
                                        <FaTimes />
                                    </Button>
                                </Col>
                                <Col md={12} className="mt-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Description (optional)"
                                        value={paramConfig.description || ""}
                                        onChange={(e) =>
                                            updateKeyValuePair(
                                                field,
                                                key,
                                                key,
                                                {
                                                    ...paramConfig,
                                                    description: e.target.value,
                                                },
                                            )
                                        }
                                        style={{
                                            backgroundColor: "#2d2d2d",
                                            borderColor: "#555",
                                            color: "#fff",
                                        }}
                                    />
                                </Col>
                            </Row>
                        );
                    },
                )}
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => addKeyValuePair(field)}
                >
                    <FaPlus className="me-1" />
                    Add {title.slice(0, -1)}
                </Button>
            </Accordion.Body>
        </Accordion.Item>
    );
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            contentClassName="bg-dark text-light"
        >
            <Modal.Header
                closeButton
                closeVariant="white"
                style={{
                    backgroundColor: "#2d2d2d",
                    borderBottom: "1px solid #444",
                }}
            >
                <Modal.Title>
                    {editingTool ? "Edit API Tool" : "Add New API Tool"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body
                style={{
                    backgroundColor: "#2d2d2d",
                    maxHeight: "70vh",
                    overflowY: "auto",
                }}
            >
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-light">
                                    Tool Name
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newTool.name}
                                    onChange={(e) =>
                                        setNewTool({
                                            ...newTool,
                                            name: e.target.value,
                                        })
                                    }
                                    placeholder="e.g., Get Order Status"
                                    style={{
                                        backgroundColor: "#1a1a1a",
                                        borderColor: "#555",
                                        color: "#fff",
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-light">
                                    HTTP Method
                                </Form.Label>
                                <Form.Select
                                    value={newTool.method}
                                    onChange={(e) =>
                                        setNewTool({
                                            ...newTool,
                                            method: e.target.value,
                                        })
                                    }
                                    style={{
                                        backgroundColor: "#1a1a1a",
                                        borderColor: "#555",
                                        color: "#fff",
                                    }}
                                >
                                    <option value="GET">GET</option>
                                    <option value="POST">POST</option>
                                    <option value="PUT">PUT</option>
                                    <option value="DELETE">DELETE</option>
                                    <option value="PATCH">PATCH</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-light">API URL</Form.Label>
                        <Form.Control
                            type="text"
                            value={newTool.url}
                            onChange={(e) =>
                                setNewTool({ ...newTool, url: e.target.value })
                            }
                            placeholder="https://api.example.com/orders/status"
                            style={{
                                backgroundColor: "#1a1a1a",
                                borderColor: "#555",
                                color: "#fff",
                            }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-light">
                            Description
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={newTool.description}
                            onChange={(e) =>
                                setNewTool({
                                    ...newTool,
                                    description: e.target.value,
                                })
                            }
                            placeholder="Describe what this tool does..."
                            style={{
                                backgroundColor: "#1a1a1a",
                                borderColor: "#555",
                                color: "#fff",
                            }}
                        />
                    </Form.Group>

                    {/* Parameter Configuration Sections */}
                    <Accordion className="mb-3">
                        {renderKeyValueSection("headers", "Headers")}
                        {renderKeyValueSection("params", "Parameters")}
                        {renderKeyValueSection("data", "Request Body")}
                    </Accordion>

                    {/* Test API Section */}
                    {newTool.url && (
                        <div className="mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <h6 className="text-light mb-0">Test API Request</h6>
                                <Button 
                                    variant="outline-info"
                                    size="sm"
                                    onClick={testApiRequest}
                                    disabled={isTestingApi || !newTool.url}
                                >
                                    {isTestingApi ? (
                                        <>
                                            <Spinner size="sm" className="me-2" />
                                            Testing...
                                        </>
                                    ) : (
                                        <>
                                            <FaPlay className="me-2" />
                                            Test Request
                                        </>
                                    )}
                                </Button>
                            </div>
                            {testResult && (
                                <Alert 
                                    variant={testResult?.success ? "success" : "danger"}
                                    className="mt-2"
                                >
                                    <div className="d-flex align-items-center mb-2">
                                        {testResult?.success ? (
                                            <FaCheckCircle className="me-2" />
                                        ) : (
                                            <FaExclamationTriangle className="me-2" />
                                        )}
                                        <strong>
                                            {testResult?.success ? 'Success' : 'Error'}
                                            {testResult?.status && ` (${testResult.status})`}
                                        </strong>
                                    </div>
                                    
                                    {testResult?.error && (
                                        <div className="mb-2">
                                            <small>{testResult.error}</small>
                                        </div>
                                    )}
                                    
                                    {testResult?.data && (
                                        <details>
                                            <summary style={{ cursor: 'pointer' }}>
                                                <small>View Response</small>
                                            </summary>
                                            <pre 
                                                style={{ 
                                                    fontSize: '12px', 
                                                    maxHeight: '200px', 
                                                    overflow: 'auto',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '10px',
                                                    borderRadius: '4px',
                                                    marginTop: '10px'
                                                }}
                                            >
                                                {JSON.stringify(testResult.data, null, 2)}
                                            </pre>
                                        </details>
                                    )}
                                </Alert>
                            )}
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer
                style={{
                    backgroundColor: "#2d2d2d",
                    borderTop: "1px solid #444",
                }}
            >
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={editingTool ? onUpdate : onSave}
                    disabled={!newTool.name || !newTool.url}
                >
                    <FaSave className="me-1" />
                    {editingTool ? "Update Tool" : "Add Tool"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ApiToolModal;
